import {ReactElement, useCallback, useMemo} from 'react';
import {
  AddToCartButton,
  Image,
  Link,
  ProductOptionsProvider,
} from '@shopify/hydrogen';
import {Product} from '@shopify/hydrogen/storefront-api-types';

import {PlaceholderWhiskyJPEG} from '../assets/img';
import {ProductEventType} from '../types';
import {formatAmount} from '../utils';
import ProductHoverButtons from './ProductHoverButtons.client';

import Skeleton from 'react-loading-skeleton';
import {flattenProduct} from '../utils/graphql';
import {useAuthService} from '../contexts/AuthServiceProvider.client';
import {MemberProductProvider} from '../contexts/MemberProductProvider.client';
import useShopifyProductMetafields from '../hooks/useShopifyProductMetafields';
import ProductTileRating from './ProductTileRating.client';
import {
  getIsVariantAvailableForSale,
  getShowcasedVariant,
} from '../utils/variants';
import {REWARDS_IB_TAG} from '../constants';
import {calculateRewardsIbSaveAmount} from '../utils/rewards';

interface ProductTileProps {
  product: Product;
  onNavigate?: () => void;
}

export default function ProductTile({
  product,
  onNavigate,
}: ProductTileProps): ReactElement {
  const flattenedProduct = useMemo(() => flattenProduct(product), [product]);

  const {authUser} = useAuthService();

  const ibSaveAmount = useMemo(() => {
    if (flattenedProduct.tags?.includes(REWARDS_IB_TAG)) {
      return calculateRewardsIbSaveAmount(flattenedProduct.variants);
    }
    return null;
  }, [flattenedProduct.tags, flattenedProduct.variants]);

  const showIbSavingsTag = useMemo(() => ibSaveAmount !== null, [ibSaveAmount]);

  const shopifyProductMetafields = useShopifyProductMetafields(
    flattenedProduct.metafields,
  );

  const {
    metafieldsKeyValue: {latestEvent, sponsoredProductVariant},
  } = shopifyProductMetafields;

  const productLatestEventBadge: ReactElement = useMemo(() => {
    if (!latestEvent) {
      return <></>;
    }

    const {type} = latestEvent;
    switch (type) {
      case ProductEventType.new:
        return <div className="product__new">NEW</div>;
      case ProductEventType.priceDown:
        return (
          <div className="product__pricetag">
            PRICE <i className="icon-bottom-arrow-3" />
          </div>
        );
      case ProductEventType.priceUp:
        return (
          <div className="product__pricetag priceUp">
            PRICE <i className="icon-bottom-arrow-3" />
          </div>
        );

      default:
        return <></>;
    }
  }, [latestEvent]);

  const showcasedVariant = useMemo(
    () =>
      getShowcasedVariant(flattenedProduct.variants, sponsoredProductVariant),
    [flattenedProduct.variants, sponsoredProductVariant],
  );

  const showcasedVariantAvailable = useMemo(
    () =>
      Boolean(showcasedVariant) &&
      getIsVariantAvailableForSale(showcasedVariant),
    [showcasedVariant],
  );

  const {findMemberProduct} = useAuthService();

  const memberProduct = useMemo(
    () => findMemberProduct(flattenedProduct.id),
    [findMemberProduct, flattenedProduct.id],
  );

  const {id, title, handle, featuredImage} = flattenedProduct;
  const {
    metafieldsKeyValue: {
      averageRating,
      buyerSavingAmount,
      type: whiskyType,
      country,
    },
  } = shopifyProductMetafields;

  const productType = useMemo(
    () => [whiskyType, country?.label].filter((value) => value).join(' - '),
    [country?.label, whiskyType],
  );

  const buyerSavingAmountInt = useMemo(() => {
    if (!buyerSavingAmount) return 0;
    return Math.round(buyerSavingAmount);
  }, [buyerSavingAmount]);

  const linkClickHandler = useCallback(() => onNavigate?.(), [onNavigate]);

  return (
    <ProductOptionsProvider data={product}>
      <div className="product js-product">
        <MemberProductProvider memberProduct={memberProduct}>
          <div className="product__top">
            <Link
              to={`/shop/${handle}`}
              className="product__image"
              onClick={linkClickHandler}
            >
              <picture>
                <Image
                  src={
                    featuredImage ? featuredImage.url : PlaceholderWhiskyJPEG
                  }
                  width={320}
                  height={400}
                  alt={title}
                />
              </picture>
            </Link>
            {buyerSavingAmountInt > 0 && !showIbSavingsTag && (
              <div className="product__savings">
                <strong>${buyerSavingAmountInt}</strong>
                <span>off</span>
              </div>
            )}
            {showIbSavingsTag ? (
              authUser?.member?.is_rewards_member ? (
                <div className="product__ib-savings tag-element">
                  Save {formatAmount(ibSaveAmount, false)} with code IB20
                </div>
              ) : (
                <Link
                  to={`/shop/${handle}#rewards`}
                  className="product__ib-savings tag-element"
                >
                  Want to save {formatAmount(ibSaveAmount, false)}?
                </Link>
              )
            ) : (
              <div className="product__tags">{productLatestEventBadge}</div>
            )}
            <ProductHoverButtons
              productId={id}
              productImage={featuredImage ? featuredImage.url : null}
              productTitle={title}
              rating={(memberProduct && memberProduct.rating) || null}
            />
          </div>
          <div className="product__info">
            <div className="product__type">{productType}</div>
            <h3 className="product__title">
              <Link to={`/shop/${handle}`} onClick={linkClickHandler}>
                {title}
              </Link>
            </h3>
            <ProductTileRating shopifyAverageRating={averageRating} />
          </div>
        </MemberProductProvider>
        <div className="product__footer">
          {showcasedVariantAvailable ? (
            <>
              <div className="product__price">
                {latestEvent &&
                showcasedVariant.title === latestEvent.externalShop ? (
                  <>
                    <div
                      className="product__currentprice"
                      style={
                        latestEvent.type === ProductEventType.priceDown
                          ? {}
                          : {borderRight: 'none'}
                      }
                    >
                      {formatAmount(showcasedVariant.price.amount)}
                    </div>
                    {latestEvent.type === ProductEventType.priceDown && (
                      <div className="product__prevprice">
                        {formatAmount(latestEvent.oldPrice)}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="product__currentprice"
                    style={{borderRight: 'none'}}
                  >
                    {formatAmount(showcasedVariant.price.amount)}
                  </div>
                )}
              </div>
              <div className="product__seller">{showcasedVariant.title}</div>
              {showcasedVariant &&
                showcasedVariantAvailable &&
                showcasedVariant.availableForSale && (
                  <AddToCartButton
                    className="product__buy button button_size_small"
                    variantId={showcasedVariant.id}
                  >
                    Buy Now
                  </AddToCartButton>
                )}
            </>
          ) : (
            'Unavailable'
          )}
        </div>
      </div>
    </ProductOptionsProvider>
  );
}

export function SkeletonProductTile() {
  return (
    <div className="product js-product">
      <div className="product__top">
        <Skeleton
          height="100%"
          width="100%"
          containerClassName="product__image"
        />
      </div>
      <div className="product__info">
        <div className="product__type">
          <Skeleton />
        </div>
        <h3 className="product__title">
          <Skeleton />
        </h3>
      </div>
      <div className="product__footer">
        <Skeleton />
      </div>
    </div>
  );
}
